import { useCallback, useMemo, useState } from "react";
import { Grid, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import AddIcon from "@mui/icons-material/Add";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import LmsSummaryCard from "@/components/lms/lmsSummaryCard";
import RoundedContentBox from "@/components/general/roundedContentBox";
import { RootState } from "@/components/state/store";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import LmsStatusOverviewCard from "@/components/lms/lmsStatusOverviewCard";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import LmsEnrolmentCreateModal from "@/components/lms/lmsEnrolmentCreateModal";
import GenericNotification from "@/components/notification/genericNotification";
import DateRangeFilterModal from "@/components/dateTime/dateRangeFilterModal";
import DateRangeFilterChip from "@/components/dateTime/dateRangeFilterChip";
import {
  formatDateValue,
  getEffectiveLmsMode,
  getLmsCourseOverviewRoute,
  getLmsEnrolmentsRoute,
  getLmsCoursesRoute,
  getLmsManagerRequestConfig,
  getLmsManagerRequestParameters,
  shouldBlockLmsForSubdomain,
} from "@/components/lms/lms.utils";
import {
  buildEnrolmentPresetsFromSubjectSearch,
  resolveOverviewMetricsScope,
  shouldShowLmsScopedContent,
} from "@/components/lms/lmsOverviewMetricsHelpers";
import {
  areLmsDashboardFilterParamsEqual,
  buildLmsDashboardDateFilterParams,
  type LmsDashboardDateField,
  type LmsDashboardStatusFilterParams,
  useLmsDashboard,
} from "@/components/lms/useLmsDashboard";
import { EMPTY_STATUS_COUNTS } from "@/components/lms/useLmsStatusOverviewCounts";
import styles from "@/routes/lms/lmsOverview.module.css";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import type {
  QuickSearchSelectionValidationState,
} from "@/types/QuickSearch";
import {
  getLmsSelectionBlockedMessage as formatLmsSelectionBlockedMessage,
  LMS_START_VERTRAG_SELECTION_ENRICHMENT,
} from "@/components/lms/lmsQuickSearchEligibility";

interface LatestCompletionItem {
  id: string;
  courseId?: string;
  learnerName: string;
  courseTitle: string;
  completedAt: string;
}

interface EnrolmentDateFilterState {
  filter: LmsDashboardDateField;
  dateRange: [Date | null, Date | null] | [];
}

const DEFAULT_ENROLMENT_DATE_FILTER: EnrolmentDateFilterState = {
  filter: "created_on",
  dateRange: [],
};

interface DashboardCardItem {
  count: number;
  title: string;
  buttonLabel?: string;
  description?: string;
  route?: string;
  secondaryButtonLabel?: string;
  secondaryButtonStartIcon?: React.ReactNode;
  onSecondaryClick?: () => void;
  progressPercent?: number;
  progressText?: string;
}

export default function LmsOverview() {
  const { t } = useTranslation();
  const [isCreateEnrolmentModalOpen, setIsCreateEnrolmentModalOpen] = useState(false);
  const [overviewRefreshKey, setOverviewRefreshKey] = useState(0);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [isDateFilterModalOpen, setIsDateFilterModalOpen] = useState(false);
  const [enrolmentDateFilter, setEnrolmentDateFilter] =
    useState<EnrolmentDateFilterState>(DEFAULT_ENROLMENT_DATE_FILTER);
  const [dashboardStatusFilterParams, setDashboardStatusFilterParams] =
    useState<LmsDashboardStatusFilterParams>({});
  const [companySelectionValidationState, setCompanySelectionValidationState] =
    useState<QuickSearchSelectionValidationState>("validating");
  const navigate = useNavigate();
  const loginState = useSelector((state: RootState) => state.login);
  const selectedChildCompany = useSelector((state: RootState) => state.parent);
  const subjectSearch = useSelector((state: RootState) => state.subjectSearch);

  const subdomain = window.location.hostname.split(".")[0];
  const shouldBlockLms = shouldBlockLmsForSubdomain({
    subdomain,
    permissions: loginState.permissions,
  });
  const effectiveMode = getEffectiveLmsMode({
    subdomain,
    permissions: loginState.permissions,
  });
  usePageHeading(
    `${t("lms_product_name")} ${subdomain === "admin"
      ? t("lms_company_details")
      : t("lms_overview")
    }`
  );
  const managerRequestConfig = useMemo(
    () =>
      getLmsManagerRequestConfig(
        effectiveMode,
        selectedChildCompany.subjectId,
      ),
    [effectiveMode, selectedChildCompany.subjectId],
  );
  const managerRequestParameters = useMemo(
    () =>
      getLmsManagerRequestParameters(
        effectiveMode,
        selectedChildCompany.subjectId,
      ),
    [effectiveMode, selectedChildCompany.subjectId],
  );
  const overviewScope = useMemo(
    () =>
      resolveOverviewMetricsScope({
        subdomain,
        mode: effectiveMode,
        subjectSearch,
        selectedChildCompanySubjectId:
          selectedChildCompany.subjectId,
      }),
    [
      effectiveMode,
      selectedChildCompany.subjectId,
      subdomain,
      subjectSearch,
    ],
  );
  const {
    presetParticipants: enrolmentPresetParticipants,
    presetCompanies: enrolmentPresetCompanies,
  } = useMemo(
    () => buildEnrolmentPresetsFromSubjectSearch(subjectSearch),
    [subjectSearch],
  );
  const showOverviewContent =
    shouldShowLmsScopedContent(effectiveMode, overviewScope) &&
    (subdomain !== "admin" || companySelectionValidationState === "valid");

  const dashboardDateFilterParams = useMemo(
    () =>
      buildLmsDashboardDateFilterParams({
        dateField: enrolmentDateFilter.filter,
        dateRange: enrolmentDateFilter.dateRange,
      }),
    [enrolmentDateFilter],
  );

  const dateFilterOptions = useMemo(
    () => [
      { value: "created_on", label: t("assigned_on") },
      { value: "started_on", label: t("started_on") },
      { value: "completed_on", label: t("completed_on") },
      {
        value: "certificate__last_downloaded_on",
        label: t("last_downloaded_on"),
      },
    ],
    [t],
  );

  const formatLatestCompletionDate = useCallback(
    (value?: string | null) => {
      if (!value) {
        return formatDateValue(value, true);
      }

      const completedAt = dayjs(value);

      if (completedAt.isSame(dayjs(), "day")) {
        return `${t("date_today")}, ${completedAt.format("HH:mm")}`;
      }

      if (completedAt.isSame(dayjs().subtract(1, "day"), "day")) {
        return `${t("date_yesterday")}, ${completedAt.format("HH:mm")}`;
      }

      return formatDateValue(value, true);
    },
    [t],
  );

  const getLmsSelectionBlockedMessage = useCallback(
    (selection: Parameters<typeof formatLmsSelectionBlockedMessage>[0]) =>
      formatLmsSelectionBlockedMessage(selection, t),
    [t],
  );

  const handleDashboardStatusFilterChange = useCallback(
    (nextFilterParams: LmsDashboardStatusFilterParams) => {
      setDashboardStatusFilterParams((currentFilterParams) =>
        areLmsDashboardFilterParamsEqual(
          currentFilterParams,
          nextFilterParams,
        )
          ? currentFilterParams
          : nextFilterParams,
      );
    },
    [],
  );
  const shouldNotImpersonate =
    managerRequestConfig.shouldNotImpersonate ?? true;
  const {
    clearError: clearDashboardError,
    data: dashboardData,
    dataStatusFilterParams: dashboardDataStatusFilterParams,
    error: dashboardError,
    isLoading: isDashboardLoading,
  } = useLmsDashboard({
    companySubjectId:
      effectiveMode === "administrator"
        ? overviewScope.companySubjectId
        : undefined,
    dateFilterParams: dashboardDateFilterParams,
    enabled:
      !shouldBlockLms &&
      effectiveMode !== "learner" &&
      showOverviewContent,
    mode: effectiveMode,
    refreshKey: overviewRefreshKey,
    scopedRequestParameters: managerRequestParameters,
    shouldNotImpersonate,
    statusFilterParams: dashboardStatusFilterParams,
  });

  const latestCompletionItems = useMemo<LatestCompletionItem[]>(() => {
    if (effectiveMode !== "manager") {
      return [];
    }

    return (dashboardData?.latest_completions ?? [])
      .slice(0, 10)
      .map((enrolment) => ({
        id: String(enrolment.id ?? ""),
        courseId: enrolment.course?.id
          ? String(enrolment.course.id)
          : undefined,
        learnerName: String(enrolment.participant?.name ?? "-"),
        courseTitle: String(enrolment.course?.name ?? "-"),
        completedAt: formatLatestCompletionDate(enrolment.completed_on),
      }));
  }, [dashboardData, effectiveMode, formatLatestCompletionDate]);

  const enrolmentCount = dashboardData?.summary.enrolments.total ?? 0;
  const completedEnrolmentCount =
    dashboardData?.summary.enrolments.completed ?? 0;
  const administratorCourseCounts = {
    enrolled: dashboardData?.summary.courses?.with_enrolled_users ?? 0,
    total: dashboardData?.summary.courses?.total_available ?? 0,
  };
  const dashboardCompanySubjectId =
    dashboardData?.company_subject ?? overviewScope.companySubjectId;
  const dashboardStatusCounts = dashboardData
    ? {
        not_started: dashboardData.status_counts.not_started,
        in_progress: dashboardData.status_counts.in_progress,
        completed: dashboardData.status_counts.completed,
      }
    : EMPTY_STATUS_COUNTS;
  const aggregatedStatusOverview = {
    clearError: clearDashboardError,
    error: dashboardError,
    isLoading: isDashboardLoading,
    onFilterParamsChange: handleDashboardStatusFilterChange,
    statusCountsFilterParams: dashboardDataStatusFilterParams,
    statusCounts: dashboardStatusCounts,
  };

  if (shouldBlockLms) {
    return <Navigate to="/" replace />;
  }

  if (effectiveMode === "learner") {
    return <Navigate to={getLmsCoursesRoute(effectiveMode)} replace />;
  }

  const enrolmentDashboardCard: DashboardCardItem = {
    count: enrolmentCount,
    title: t("total_course_enrolments"),
    buttonLabel: t("view_enrolments"),
    secondaryButtonLabel:
      effectiveMode === "administrator" ? undefined : t("add_enrolment"),
    secondaryButtonStartIcon:
      effectiveMode === "administrator" ? undefined : <AddIcon />,
    route: getLmsEnrolmentsRoute(effectiveMode),
    onSecondaryClick:
      effectiveMode === "administrator"
        ? undefined
        : () => setIsCreateEnrolmentModalOpen(true),
    progressPercent:
      enrolmentCount > 0
        ? (completedEnrolmentCount / enrolmentCount) * 100
        : 0,
    progressText: t("completed_enrolments_progress", {
      completed: completedEnrolmentCount,
      total: enrolmentCount,
    }),
  };

  const dashboardCards: DashboardCardItem[] =
    effectiveMode === "administrator"
      ? [
        enrolmentDashboardCard,
        {
          count: administratorCourseCounts.enrolled,
          title: t("enrolled_training"),
          secondaryButtonLabel: t("add_enrolment"),
          secondaryButtonStartIcon: <AddIcon />,
          onSecondaryClick: () => setIsCreateEnrolmentModalOpen(true),
          description:
            administratorCourseCounts.enrolled > 0
              ? t("enrolled_training_summary", {
                  count: administratorCourseCounts.enrolled,
                  total: administratorCourseCounts.total,
                })
              : t("enrolled_training_summary_empty", {
                  total: administratorCourseCounts.total,
                }),
        },
      ]
      : [enrolmentDashboardCard];

  const isDateFilterActive = Boolean(
    enrolmentDateFilter.dateRange?.[0] || enrolmentDateFilter.dateRange?.[1],
  );

  const handleDateRangeChange = (dateRange: [Date | null, Date | null]) => {
    setEnrolmentDateFilter((previous) => ({ ...previous, dateRange }));
  };

  const handleFilterFieldChange = (filter: LmsDashboardDateField) => {
    setEnrolmentDateFilter((previous) => ({ ...previous, filter }));
  };

  const handleClearDateFilter = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setEnrolmentDateFilter(DEFAULT_ENROLMENT_DATE_FILTER);
  };

  const navigateDateRange = (direction: "left" | "right") => {
    const [start, end] = enrolmentDateFilter.dateRange ?? [];
    if (!start || !end) {
      return;
    }

    const rangeLengthInDays = dayjs(end).diff(dayjs(start), "day") + 1;
    const offset = direction === "left" ? -rangeLengthInDays : rangeLengthInDays;

    setEnrolmentDateFilter((previous) => ({
      ...previous,
      dateRange: [
        dayjs(start).add(offset, "day").toDate(),
        dayjs(end).add(offset, "day").toDate(),
      ],
    }));
  };

  const dateFilterFieldLabel =
    dateFilterOptions.find(
      (option) => option.value === enrolmentDateFilter.filter,
    )?.label ?? "";

  const renderSummaryCard = (card: DashboardCardItem) => {
    const route = card.route;

    return (
      <LmsSummaryCard
        count={card.count}
        title={card.title}
        buttonLabel={card.buttonLabel}
        description={card.description}
        onClick={route ? () => navigate(route) : undefined}
        secondaryButtonLabel={card.secondaryButtonLabel}
        secondaryButtonStartIcon={card.secondaryButtonStartIcon}
        onSecondaryClick={card.onSecondaryClick}
        progressPercent={card.progressPercent}
        progressText={card.progressText}
      />
    );
  };

  const latestCompletionsCard = effectiveMode === "manager" && (
    <RoundedContentBox className={styles.latestCompletionsCard}>
      <h3 className={styles.cardTitle}>
        {t("latest_course_completions")}
      </h3>
      <div className={styles.latestCompletionsList}>
        {latestCompletionItems.map((item, index) => (
          <div
            key={item.id}
            className={styles.latestCompletionRow}
            style={{
              borderBottom:
                index === latestCompletionItems.length - 1
                  ? "none"
                  : "1px solid #f0ebf5",
            }}
          >
            <div className={styles.latestCompletionContent}>
              <p className={styles.latestCompletionLearner}>{item.learnerName}</p>
              <p className={styles.latestCompletionCourse}>{item.courseTitle}</p>
              <p className={styles.latestCompletionTime}>{item.completedAt}</p>
            </div>
            <div className={styles.latestCompletionActions}>
              <Tooltip
                arrow
                placement="top"
                title={t("view_course")}
              >
                <span>
                  <GenericButton
                    variant="icon"
                    color="iconBase"
                    onClick={() => {
                      if (!item.courseId) {
                        return;
                      }
                      navigate(
                        getLmsCourseOverviewRoute(
                          effectiveMode,
                          item.courseId,
                        ),
                      );
                    }}
                    aria-label={t("view_course")}
                  >
                    <MenuBookIcon />
                  </GenericButton>
                </span>
              </Tooltip>
            </div>
          </div>
        ))}
        {latestCompletionItems.length === 0 && (
          <div className={styles.latestCompletionRow}>
            <div className={styles.latestCompletionContent}>
              <p className={styles.latestCompletionCourse}>
                {t("no_completed_enrolments_to_show")}
              </p>
            </div>
          </div>
        )}
      </div>
    </RoundedContentBox>
  );

  const dateFilterControls = (
    <div className={styles.dateFilterControls}>
      <GenericButton
        onClick={() => setIsDateFilterModalOpen(true)}
        startIcon={<TuneOutlinedIcon />}
      >
        {t("apply_filter")}
      </GenericButton>
      {isDateFilterActive && (
        <DateRangeFilterChip
          filterLabel={dateFilterFieldLabel}
          startDate={enrolmentDateFilter.dateRange?.[0]}
          endDate={enrolmentDateFilter.dateRange?.[1]}
          onDelete={handleClearDateFilter}
          onClick={() => setIsDateFilterModalOpen(true)}
          onNavigateLeft={() => navigateDateRange("left")}
          onNavigateRight={() => navigateDateRange("right")}
        />
      )}
    </div>
  );

  return (
    <Grid container marginBottom={3} rowSpacing={"1rem"}>
      {(subdomain === "admin" || showOverviewContent) && (
        <Grid item xs={12}>
          <div
            className={`${styles.topToolbar} ${
              effectiveMode === "manager" ? styles.topToolbarManager : ""
            } ${
              subdomain === "admin" ? styles.topToolbarAdministrator : ""
            }`}
          >
            {subdomain === "admin" && (
              <div className={styles.dropDownSubjectSearch}>
                <FormFieldLabel
                  label={t("company")}
                  htmlFor="kaer-learning-company-filter"
                />
                <DropDownSubjectSearch
                  name="kaer-learning-company-filter"
                  subdomain={subdomain}
                  options={[]}
                  onChange={() => {
                    /* no-op */
                  }}
                  useGlobalState={true}
                  isClearable={true}
                  queryParameter="companies"
                  selectionEnrichment={
                    LMS_START_VERTRAG_SELECTION_ENRICHMENT
                  }
                  getSelectionBlockedMessage={getLmsSelectionBlockedMessage}
                  onSelectionValidationChange={
                    setCompanySelectionValidationState
                  }
                />
              </div>
            )}
            {showOverviewContent && dateFilterControls}
          </div>
        </Grid>
      )}
      {showOverviewContent && (
        <>
          {effectiveMode === "manager" ? (
            <Grid
              container
              item
              columnSpacing={"1rem"}
              rowSpacing={"1rem"}
              alignItems="stretch"
            >
              <Grid item xs={12} md={6}>
                <div className={styles.managerLeftColumn}>
                  {dashboardCards.map((card) => (
                    <div key={card.title}>{renderSummaryCard(card)}</div>
                  ))}
                  <div className={styles.managerStatusCard}>
                    <LmsStatusOverviewCard
                      aggregatedDashboard={aggregatedStatusOverview}
                      mode={effectiveMode}
                      companySubjectId={dashboardCompanySubjectId}
                    />
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} md={6} style={{ display: "flex" }}>
                {latestCompletionsCard}
              </Grid>
            </Grid>
          ) : (
            <>
              <Grid container item columnSpacing={"1rem"} rowSpacing={"1rem"}>
                {dashboardCards.map((card) => (
                  <Grid item xs={12} md={6} key={card.title}>
                    {renderSummaryCard(card)}
                  </Grid>
                ))}
              </Grid>
              <Grid
                container
                item
                columnSpacing={"1rem"}
                rowSpacing={"1rem"}
                alignItems="stretch"
              >
                <Grid item xs={12} style={{ display: "flex" }}>
                  <LmsStatusOverviewCard
                    aggregatedDashboard={aggregatedStatusOverview}
                    mode={effectiveMode}
                    companySubjectId={dashboardCompanySubjectId}
                    dualDisplayMode={effectiveMode === "administrator"}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}
      {isDateFilterModalOpen && (
        <DateRangeFilterModal
          open={isDateFilterModalOpen}
          initialDateRange={enrolmentDateFilter.dateRange}
          initialFilter={enrolmentDateFilter.filter}
          filterOptions={dateFilterOptions}
          allowSingleDate={true}
          onClose={() => setIsDateFilterModalOpen(false)}
          onApply={(dateRange, filter) => {
            handleDateRangeChange(dateRange);
            if (filter) {
              handleFilterFieldChange(filter as LmsDashboardDateField);
            }
            setIsDateFilterModalOpen(false);
          }}
        />
      )}
      <LmsEnrolmentCreateModal
        open={isCreateEnrolmentModalOpen}
        mode={effectiveMode}
        onClose={() => setIsCreateEnrolmentModalOpen(false)}
        onCompleted={() => {
          setIsCreateEnrolmentModalOpen(false);
          setOverviewRefreshKey((currentValue) => currentValue + 1);
          setNotificationMessage(t("users_enrolled_successfully"));
        }}
        enableCourseSelection={true}
        presetParticipants={enrolmentPresetParticipants}
        presetCompanies={enrolmentPresetCompanies}
      />
      {notificationMessage && (
        <GenericNotification
          message={notificationMessage}
          variant="success"
          handleCloseSnackbar={() => setNotificationMessage("")}
        />
      )}
    </Grid>
  );
}
